import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AiOutlineDelete } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
// constants
import { COIN, getProductStatus } from '../../../constants';
// utils
import { getTotalPriceDiscountByArticle } from 'utils/orderFunctions';
import { formatOrderLineBookConditionDisplay } from 'utils/bookCondition';
// components
import SearchProduct from 'components/backoffice/customInput/SearchProduct';
import Select from 'components/backoffice/selects/SelectCustom';
import Button from 'components/backoffice/buttonCustom';
import Input from 'components/backoffice/customInput';

const ProductOrder = ({
  id,
  article,
  control,
  onDelete,
  setValue,
  watchedFields
}) => {
  const [articleStatus, setArticleStatus] = useState([]);
  const { t } = useTranslation(['backoffice', 'detalle']);
  const rowValues = useWatch({ control, name: `items.${id}` }) ?? {};

  const translateConditionLabel = (k) => {
    const fromDetalle = t(k, { ns: 'detalle' });
    if (fromDetalle && fromDetalle !== k) return fromDetalle;
    return t(`orderTrans.${k}`, { ns: 'backoffice' });
  };

  useEffect(() => {
    setArticleStatus(getProductStatus(t));
  }, []);

  useEffect(() => {
    const total = getTotalPriceDiscountByArticle(watchedFields.items[id].quantity, watchedFields.items[id].price, watchedFields.items[id].discount)
    setValue(`items.${id}.total_price`, total)
  }, [watchedFields.items[id].price, watchedFields.items[id].quantity, watchedFields.items[id].discount]);

  return (
    <Row className="pt-3">
      {['ean', 'titulo'].map((el) => (
        <Col md={2} className="d-flex align-bottom mb-4 mb-lg-0" key={el}>
          <Controller
            key={article.id}
            control={control}
            name={`items.${id}.product`}
            render={({ field: { onChange, value } }) => (
              <div className="w-100 orders" data-testid={`autocomplete_${el}`}>
                <SearchProduct
                  fields="id,title,image,ean,editorial,price,type,has_used,has_newbalance,has_stock"
                  filterBy={el}
                  onSelected={(item) => {
                    onChange(item);
                    setValue(`items.${id}.editorial`, item && item.editorial ? item.editorial.nombre_fiscal : '');
                    setValue(`items.${id}.price`, item && item.price != null ? item.price.toFixed(2) : '');
                    setValue(`items.${id}.book_condition`, item?.book_condition ?? item?.bookCondition);
                  }}
                  defaultArticle={value}
                  placeholder={t(el === 'titulo' ? 'name' : el)}
                  includeInactive={true}
                />
              </div>
            )}
          />
        </Col>
      ))}
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Controller
          key={article.id}
          control={control}
          name={`items.${id}.editorial`}
          defaultValue={article.editorial?.nombre_fiscal || ''}
          render={({ field }) => (
            <Input
              readOnly={true}
              type="text"
              value={field.value}
              placeholder={t('product.editorial')}
              dataTestId="editorial"
            />
          )}
        />
      </Col>
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Controller
          key={article.id}
          control={control}
          name={`items.${id}.price`}
          defaultValue={article.precio_venta}
          render={({ field }) => (
            <Input
              readOnly={true}
              type="number"
              placeholder={COIN}
              value={field.value}
              dataTestId="price"
            />
          )}
        />
      </Col>
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Controller
          key={article.id}
          control={control}
          name={`items.${id}.quantity`}
          defaultValue={article.quantity || 1}
          render={({ field: { value, onChange } }) => (
            <Input
              min={1}
              type="number"
              defaultValue={value}
              onChange={onChange}
              dataTestId="quantity"
            />
          )}
        />
      </Col>
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Controller
          key={article.id}
          control={control}
          name={`items.${id}.discount`}
          render={({ field: { value, onChange } }) => (
            <Input
              type="number"
              placeholder="%"
              value={value}
              onChange={onChange}
              min={0}
            />
          )}
        />
      </Col>
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Controller
          key={article.id}
          control={control}
          name={`items.${id}.total_price`}
          defaultValue={article.total_price}
          render={({ field }) => (
            <Input
              type="number"
              placeholder={COIN}
              value={field.value}
              readOnly={true}
              dataTestId="total_price"
            />
          )}
        />
      </Col>
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Input
          readOnly={true}
          type="text"
          value={formatOrderLineBookConditionDisplay(rowValues, translateConditionLabel)}
          dataTestId="book_condition_display"
        />
      </Col>
      <Col md={1} className="d-flex align-bottom mb-4 mb-lg-0">
        <Controller
          key={article.id}
          control={control}
          name={`items.${id}.state`}
          defaultValue={article.estado}
          render={({ field: { value, onChange } }) => (
            <Select
              choices={articleStatus}
              onCheckedItem={onChange}
              value={value}
            />
          )}
        />
      </Col>
      <Col md={1} className="pt-3">
        <Button color="secondary" onClick={onDelete} type={'button'} extraClass="product-order-delete-btn">
          <AiOutlineDelete />
        </Button>
      </Col>
    </Row>
  );
};

ProductOrder.propTypes = {
  article: PropTypes.any,
  control: PropTypes.any,
  id: PropTypes.any,
  onDelete: PropTypes.func,
  setValue: PropTypes.func,
  watchedFields: PropTypes.any,
};

export default ProductOrder;
